import { useCallback, useEffect, useMemo, useRef } from 'react'
import braintree, { type Client } from 'braintree-web'
import { withTimeout } from '@mobi/utils'

export function useBraintreeClient(token: string) {
  const client = useRef<Client | undefined>(undefined)
  const isReadyResolve = useRef<(isReady: true) => void | undefined>(undefined)
  const isReadyPromise = useMemo<Promise<boolean>>(
    () =>
      new Promise<boolean>(resolve => {
        isReadyResolve.current = resolve
      }),
    []
  )
  const isReady = useCallback(() => withTimeout(isReadyPromise, 5_000, false), [isReadyPromise])

  useEffect(() => {
    // NOTE: a Braintree `Client` instance's merchant/gateway configuration is
    // fixed at creation time - setting `.authorization` on an existing
    // instance does NOT re-fetch/re-apply configuration for the new token, it
    // silently keeps using whichever merchant account it was originally
    // created with. So whenever `token` changes (e.g. `mustUse3DSecure` flips
    // once the user enters an amount over `minimumLiabilityShiftThreshold`),
    // we must always create a brand new client rather than reuse/mutate the
    // existing one.
    braintree.client
      .create({
        authorization: token,
      })
      .then(clientInstance => {
        client.current = clientInstance
        isReadyResolve.current?.(true)
      })
  }, [token])

  return {
    client,
    isReady,
  } as const
}
